import * as React from "react";
import { Tabs, Tab, Typography, Box, Stack } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { useNavigate } from "react-router-dom";
import logoSrc from "../../assets/favicon.svg";
import SideMenuMobile from "./SideMenuMobile";
import { MenuButton } from "../Common";

export default function Header({
  currentPath,
  actionsSlot,
}: {
  currentPath: string;
  actionsSlot?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setMobileMenuOpen(newOpen);
  };

  const pageConfig: Record<
    string,
    {
      title: string;
      subtitle?: string;
      tabs?: { label: string; path: string }[];
      actions?: { label: string; onClick: () => void; variant?: "contained" | "outlined" | "text" }[];
    }
  > = {
    "/": {
      title: "Dashboard",
      subtitle: "Your financial overview at a glance",
      actions: [
        {
          label: "New Report",
          onClick: () => navigate("/reports/new"),
          variant: "contained",
        },
      ],
    },
    "/transactions": {
      title: "Transactions",
      subtitle: "Browse, search, and manage your transactions",
      actions: [],
    },
    "/accounts": {
      title: "Accounts",
      subtitle: "Manage your linked bank and financial accounts",
    },
    "/budget": {
      title: "Budget",
      subtitle: "Set spending limits and track your progress",
    },
    "/goals": {
      title: "Goals",
      subtitle: "Track your savings goals and milestones",
    },
    "/reports": {
      title: "Reports",
      subtitle: "Analyse trends and insights across your finances",
    },
    "/jars": {
      title: "Jars",
      subtitle: "Organise and share your money jars",
      actions: [],
    },
  };

  const matchedKey =
    Object.keys(pageConfig)
      .sort((a, b) => b.length - a.length)
      .find((key) => currentPath.startsWith(key)) || "/";
  const config = pageConfig[matchedKey];

  const hasTabs = Boolean(config?.tabs?.length);
  const currentTab =
    config?.tabs?.findIndex((t) => currentPath === t.path) ?? false;

  return (
    <Box
      sx={(theme) => ({
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        width: "100%",
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        backgroundImage: "none",
        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
        backdropFilter: "blur(10px)",
      })}
    >
      <Stack
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 1.5, md: 2.5 },
          gap: { xs: 1.5, md: 2 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, md: 2 }}
        >
          <Stack spacing={{ xs: 1.25, md: 0 }} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ minWidth: 0 }}>
              <MenuButton
                aria-label="Open navigation menu"
                onClick={toggleDrawer(true)}
                sx={{ display: { xs: "inline-flex", md: "none" }, mt: 0.25 }}
              >
                <MenuRoundedIcon />
              </MenuButton>
              <Box
                component="img"
                src={logoSrc}
                alt="MoneyJar"
                sx={{ display: { xs: "block", md: "none" }, width: 28, height: 28, mt: 0.5 }}
              />
              <Stack direction="column" spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.2,
                    fontSize: { xs: "1.4rem", sm: "1.75rem", md: "2rem" },
                  }}
                >
                  {config?.title}
                </Typography>
                {config?.subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.5,
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {config.subtitle}
                  </Typography>
                )}
              </Stack>
            </Stack>

            {hasTabs && (
              <Tabs
                value={currentTab !== false ? currentTab : false}
                onChange={(_, newValue) => navigate(config.tabs![newValue].path)}
                textColor="primary"
                indicatorColor="primary"
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  maxWidth: "100%",
                  "& .MuiTabs-flexContainer": {
                    gap: 0.5,
                  },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    textTransform: "none",
                    fontSize: "0.9rem",
                    py: 0.5,
                  },
                }}
              >
                {config?.tabs?.map((tab) => (
                  <Tab key={tab.path} label={tab.label} />
                ))}
              </Tabs>
            )}
          </Stack>

          {actionsSlot && (
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: { xs: "stretch", md: "flex-end" },
                width: { xs: "100%", md: "auto" },
                minWidth: 0,
                overflowX: { xs: "visible", md: "auto" },
                "& > *": {
                  width: { xs: "100%", md: "auto" },
                },
                "& .MuiStack-root": {
                  width: { xs: "100%", md: "auto" },
                },
                [theme.breakpoints.down("sm")]: {
                  "& .MuiStack-root": {
                    flexDirection: "column",
                    flexWrap: "nowrap",
                    alignItems: "stretch",
                  },
                  "& .MuiButton-root": {
                    width: "100%",
                    justifyContent: "flex-start",
                  },
                },
              })}
            >
              {actionsSlot}
            </Box>
          )}
        </Stack>
      </Stack>

      <SideMenuMobile open={mobileMenuOpen} toggleDrawer={toggleDrawer} />
    </Box>
  );
}
